import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  ListItem,
  ListItemText,
  Button,
  CircularProgress, Tabs, Tab,
  List,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Layout from "../layout/layout";
import { api } from "../services/api";
import { useSelectedProvider } from "../services/selectedProiderContext";

/* icons */
import ec2Image from "../assets/Res_Amazon-EC2_Instances_48.svg";
import s3Image from "../assets/Res_Amazon-Simple-Storage-Service_Bucket_48.svg";
import securityHubImage from "../assets/Res_AWS-Security-Hub_Finding_48.svg";
import rdsImage from "../assets/Res_Amazon-Aurora_Amazon-RDS-Instance_48.svg";
import auroraImage from "../assets/Res_Amazon-Aurora-Instance_48.svg";
import efsImage from "../assets/Res_Amazon-Elastic-File-System_EFS-Intelligent-Tiering_48.svg";
import eksImage from "../assets/Res_Amazon-Elastic-Kubernetes-Service_EKS-on-Outposts_48.svg";
import lambdaImage from "../assets/Res_AWS-Lambda_Lambda-Function_48.svg";
import elasticBeanstalkImage from "../assets/Res_AWS-Elastic-Beanstalk_Application_48.svg";
import { Tooltip, IconButton } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CloudUploadIcon   from '@mui/icons-material/CloudUpload';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import RouteImage from "../assets/Res_Amazon-Route-53_Readiness-Checks_48.svg";
import albImage from "../assets/Res_Elastic-Load-Balancing_Application-Load-Balancer_48.svg";
import nlbImage from "../assets/Res_Elastic-Load-Balancing_Network-Load-Balancer_48.svg";
import azureResourceGroupsImage from "../assets/resource-groups.svg";
import azureStorageAccountsImage from "../assets/storage-accounts-classic.svg";
import azureVirtualMachinesImage from "../assets/vm-symbol.png";
import PostAddIcon from '@mui/icons-material/PostAdd';

import clsx from "clsx";


const Services = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { selectedProvider } = useSelectedProvider();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [visibleResources, setVisibleResources] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      // Get provider ID based on selected provider from context
      let cloudProviderId;
      if (selectedProvider?.toLowerCase() === 'aws') {
        cloudProviderId = 2;
      } else if (selectedProvider?.toLowerCase() === 'azure') {
        cloudProviderId = 1;
      } else {
        const storedId = localStorage.getItem("selectedProviderId");
        if (!storedId) {
          console.warn("No provider ID available");
          setLoading(false);
          return;
        }
        cloudProviderId = storedId;
      }

      let serviceId = localStorage.getItem("catalogueSelection");
      
      if (serviceId === "New") serviceId = 1;
      else if (serviceId === "Operational") serviceId = 2;
      else {
        console.warn(" No catalogue selection found. Available:", localStorage.getItem("catalogueSelection"));
        setLoading(false);
        return;
      }

      try {
        const response = await api.get("/api/Application/popularServices", {
          params: {
            cloudProviderId,
            serviceId,
            page: 1,
            pageSize: 50,
          },
        });

        if (!response.data || response.data.length === 0) {
          console.warn(" No services returned from API");
          setCategories([]);
          setSelectedCategory(null);
          setLoading(false);
          return;
        }

        const grouped = response.data.reduce((acc, service) => {
          const category = service.category;
          if (!acc[category]) {
            acc[category] = {
              categoryName: category,
              resources: [],
            };
          }
          acc[category].resources.push(service);
          return acc;
        }, {});

        const categoryArray = Object.values(grouped);
        const visibleMap = {};
        categoryArray.forEach(c => (visibleMap[c.categoryName] = 5));

        setCategories(categoryArray);
        setSelectedCategory(categoryArray[0]);
        setVisibleResources(visibleMap);
      } catch (e) {
        console.error("Error fetching services:", e);
        setCategories([]);
        setSelectedCategory(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [selectedProvider]);

  const handleResourceClick = (resource) => {
    if (resource.isEnabledonUI) {
      navigate("/Resources/View_Resources", {
        state: {
          resourceId: resource.resourceId,
          resourceName: resource.resourceName,
          provider: resource.cloudProvider,
        },
      });
    }
  };

  const handleViewMore = (categoryName) => {
    setVisibleResources(prev => ({
      ...prev,
      [categoryName]: prev[categoryName] + 5,
    }));
  };

  const getResourceImage = (name, provider) => {
    const n = name.toLowerCase();
    const isAzure = localStorage.getItem("selectedProvider")?.toLowerCase() === "azure";

    if (isAzure) {
      if (n.includes("resource group")) return azureResourceGroupsImage;
      if (n.includes("storage account") || n.includes("storage")) return azureStorageAccountsImage;
      if (n.includes("virtual machine") || n.includes("virtual machines")) return azureVirtualMachinesImage;
    }

    if (n.includes("ec2") || n.includes("instance")) return ec2Image;
    if (n.includes("s3") || n.includes("bucket")) return s3Image;
    if (n.includes("security")) return securityHubImage;
    if (n.includes("aurora")) return auroraImage;
    if (n.includes("rds")) return rdsImage;
    if (n.includes("lambda")) return lambdaImage;
    if (n.includes("efs")) return efsImage;
    if (n.includes("eks") || n.includes("kubernetes")) return eksImage;
    if (n.includes("beanstalk")) return elasticBeanstalkImage;
    if (n.includes('route 53') || n.includes('route53') || n.includes('dns')) return RouteImage;
    if (n.includes('alb') || n.includes('application load balancer')) return albImage;
    if (n.includes('nlb') || n.includes('network load balancer')) return nlbImage;
    return ec2Image;
  };

  return (
    <Layout pageType="sideBar">
      <Box height="100%" className="resource">
        {loading ? (
          <CircularProgress />
        ) : (
          <>
            {/* Page title */}
            {/* <Typography px={2} py={1} textTransform="capitalize">
            Resources
          </Typography> */}

           <Box sx={{display: "flex", justifyContent: "space-between"}}>
             {/* Tabs */}
            <Box>
              <Tabs
                value={selectedCategory?.categoryName || false}
                onChange={(e, value) => {
                  const category = categories.find(
                    (c) => c.categoryName === value
                  );
                  setSelectedCategory(category);
                }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {categories.map((cat) => (
                  <Tab
                    key={cat.categoryName}
                    label={cat.categoryName}
                    value={cat.categoryName}
                    sx={{ textTransform: "capitalize", fontSize: 14 }}
                  />
                ))}
              </Tabs>
            </Box>
            <Box px={2} py={1}>
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate("/Resources/View_Resources")}
                sx={{px:2, py:1, textTransform:"capitalize"}}>
                View all resources
              </Button>
            </Box>
           </Box>

            {/* Cards grid */}
            <Box className="resource-grid">
              {selectedCategory?.resources.map((resource) => (
                <ListItem
                  key={resource.resourceId}
                  className={clsx("resource-card", {
                    enabled: resource.isEnabledonUI,
                    disabled: !resource.isEnabledonUI,
                  })}
                >
                  <Box
                    component="img"
                    src={getResourceImage(resource.resourceName, resource.cloudProvider)}
                    sx={{ width: 42, height: 42, mb: 1 }}
                  />

                  <ListItemText
                    primary={resource.resourceName}
                    sx={{ flex: 0 }}
                    primaryTypographyProps={{
                      fontSize: 14,
                      textTransform: "capitalize",
                      textAlign: "center",
                      fontWeight: "600",
                    }}
                  />

                  {resource.isEnabledonUI && (
                    <>
                      <Box className="resource-overlay" />

                      <Box className="resource-actions">
                        <Tooltip title="Create New" placement="left" arrow>
                          <IconButton
                            className="resource-action-icon"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // prevents card click
                              navigate("/Resources/Create_Request", {
                                state: {
                                  provider: resource.cloudProvider,
                                  resourceId: resource.resourceId,
                                  resourceName: resource.resourceName,
                                },
                              });
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="View / Modify" placement="left" arrow>
                          <IconButton
                            className="resource-action-icon"
                            size="small"
                             onClick={() =>
                    resource.isEnabledonUI &&
                    navigate("/Resources/View_Resources", {
                      state: {
                        resourceId: resource.resourceId,
                        resourceName: resource.resourceName,
                        provider: resource.cloudProvider,
                      },
                    })
                  }
                          >
                            <CalendarViewWeekIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Template Gallery" placement="left" arrow>
                         <IconButton
    className="resource-action-icon"
    size="small"
    onClick={(e) => {
      e.stopPropagation(); // prevent card click

      navigate(
        `/Resources/View_Resources/template-gallery` +
          `?resourceId=${resource.resourceId}` +
          `&provider=${localStorage.getItem("selectedProvider")}` +
          `&resourceName=${encodeURIComponent(resource.resourceName)}`
      );
    }}
  >
                            <CollectionsBookmarkIcon fontSize="small" />
                          </IconButton>

                        </Tooltip>

                        {resource.resourceId === 4193 &&  <Tooltip title="Bulk EC2 Upload" placement="left" arrow>
                          <IconButton
                            className="resource-action-icon"
                            size="small"
                             onClick={() =>
                    resource.isEnabledonUI &&
                    navigate("/bulkEC2Provisioning",{
        state: {
          resourceId: resource.resourceId,
          // resourceName: resource.resourceName
        }
      }
)
                  }
                          >
                            <PostAddIcon  fontSize="small" />
                          </IconButton>
                        </Tooltip>}  
                      </Box>

                    </>
                  )}
                </ListItem>
              ))}
            </Box>
          </>
        )}
      </Box>
    </Layout>
  );

};

export default Services;
